/**
 * useBuilderSettings — settings panel wiring for resume metadata.
 *
 * Returns ready-to-spread prop bundles for SettingsPanel sub-components:
 * ThemePicker, TypographyControls, PageSettings, LayoutEditor.
 * Also provides template items for both the modal and the settings panel.
 *
 * Extend: add new metadata adapters here (e.g. CSS overrides, notes).
 */
import { t } from "@lingui/macro";
import type { Section } from "@reactive-resume/schema";
import { defaultMetadata } from "@reactive-resume/schema";
import { hasEntitlement, isProTemplate, templatesList } from "@reactive-resume/utils";
import get from "lodash.get";
import { useCallback, useMemo } from "react";

import { useMe } from "@/client/hooks/use-me";
import { useToast } from "@/client/hooks/use-toast";
import { useResumeStore } from "@/client/stores/resume";

import type { HeaderControlsProps } from "../settings/header-controls";
import type { HeadingsControlsProps } from "../settings/headings-controls";
import type { LayoutColumn, LayoutSection } from "../settings/layout-editor";
import type { LinksControlsProps } from "../settings/links-controls";
import type { PageFormat } from "../settings/page-settings";
import type { SettingsTemplate } from "../settings/settings-panel";
import type { SpacingControlsProps } from "../settings/spacing-controls";
import type { FontOption } from "../settings/typography-controls";

/* eslint-disable lingui/no-unlocalized-strings -- font names are proper nouns */
const FONT_OPTIONS: FontOption[] = [
  {
    id: "IBM Plex Serif",
    label: "IBM Plex Serif",
    stack: "'IBM Plex Serif', serif",
    kind: "Serif",
  },
  {
    id: "IBM Plex Sans",
    label: "IBM Plex Sans",
    stack: "'IBM Plex Sans', sans-serif",
    kind: "Sans",
  },
  { id: "Lato", label: "Lato", stack: "'Lato', sans-serif", kind: "Sans" },
  { id: "Lora", label: "Lora", stack: "'Lora', serif", kind: "Serif" },
  { id: "Open Sans", label: "Open Sans", stack: "'Open Sans', sans-serif", kind: "Sans" },
  {
    id: "Roboto Condensed",
    label: "Roboto Condensed",
    stack: "'Roboto Condensed', sans-serif",
    kind: "Sans",
  },
  { id: "Merriweather", label: "Merriweather", stack: "'Merriweather', serif", kind: "Serif" },
  {
    id: "Playfair Display",
    label: "Playfair Display",
    stack: "'Playfair Display', serif",
    kind: "Serif",
  },
];
/* eslint-enable lingui/no-unlocalized-strings */

export const useBuilderSettings = () => {
  const setValue = useResumeStore((s) => s.setValue);
  const metadata = useResumeStore((s) => s.resume.data.metadata);
  const sections = useResumeStore((s) => s.resume.data.sections);
  const { data: me } = useMe();
  const { toast } = useToast();

  const tier = (
    (me?.subscription as { tier?: string; plan?: string } | undefined)?.tier ??
    (me?.subscription as { tier?: string; plan?: string } | undefined)?.plan ??
    "FREE"
  ).toUpperCase();

  // ── Theme ──
  const onPrimaryChange = useCallback(
    (hex: string) => {
      setValue("metadata.theme.primary", hex);
    },
    [setValue],
  );
  const onBackgroundChange = useCallback(
    (hex: string) => {
      setValue("metadata.theme.background", hex);
    },
    [setValue],
  );
  const onTextChange = useCallback(
    (hex: string) => {
      setValue("metadata.theme.text", hex);
    },
    [setValue],
  );

  const themeProps = useMemo(
    () => ({
      primary: metadata.theme.primary,
      onPrimaryChange,
      background: metadata.theme.background,
      onBackgroundChange,
      text: metadata.theme.text,
      onTextChange,
    }),
    [
      metadata.theme.primary,
      metadata.theme.background,
      metadata.theme.text,
      onPrimaryChange,
      onBackgroundChange,
      onTextChange,
    ],
  );

  // ── Typography ──
  const onFontChange = useCallback(
    (id: string) => {
      setValue("metadata.typography.font.family", id);
      setValue("metadata.typography.font.subset", "latin");
      setValue("metadata.typography.font.variants", ["regular"]);
    },
    [setValue],
  );
  const onFontSizeChange = useCallback(
    (size: number) => {
      setValue("metadata.typography.font.size", size);
    },
    [setValue],
  );
  const onLineHeightChange = useCallback(
    (value: number) => {
      setValue("metadata.typography.lineHeight", value);
    },
    [setValue],
  );

  const typographyProps = useMemo(
    () => ({
      fontFamily: metadata.typography.font.family,
      fontOptions: FONT_OPTIONS,
      onFontChange,
      fontSize: metadata.typography.font.size,
      onFontSizeChange,
      lineHeight: metadata.typography.lineHeight,
      onLineHeightChange,
    }),
    [
      metadata.typography.font.family,
      metadata.typography.font.size,
      metadata.typography.lineHeight,
      onFontChange,
      onFontSizeChange,
      onLineHeightChange,
    ],
  );

  // ── Page ──
  const onFormatChange = useCallback(
    (format: PageFormat) => {
      setValue("metadata.page.format", format);
    },
    [setValue],
  );
  const onMarginChange = useCallback(
    (margin: number) => {
      setValue("metadata.page.margin", margin);
    },
    [setValue],
  );
  const onPageNumbersChange = useCallback(
    (value: boolean) => {
      setValue("metadata.page.options.pageNumbers", value);
    },
    [setValue],
  );

  const pageProps = useMemo(
    () => ({
      format: metadata.page.format as PageFormat,
      onFormatChange,
      margin: metadata.page.margin,
      onMarginChange,
      pageNumbers: metadata.page.options.pageNumbers,
      onPageNumbersChange,
    }),
    [
      metadata.page.format,
      metadata.page.margin,
      metadata.page.options.pageNumbers,
      onFormatChange,
      onMarginChange,
      onPageNumbersChange,
    ],
  );

  // ── Layout ──
  const layout = metadata.layout;

  const layoutSections: LayoutSection[] = useMemo(() => {
    const result: LayoutSection[] = [];
    for (const page of layout) {
      for (const key of page[0] ?? []) {
        const section = get(sections, key) as Section | undefined;
        result.push({
          id: key,
          name: section?.name ?? key,
          column: "main",
          visible: section?.visible ?? true,
        });
      }
      for (const key of page[1] ?? []) {
        const section = get(sections, key) as Section | undefined;
        result.push({
          id: key,
          name: section?.name ?? key,
          column: "sidebar",
          visible: section?.visible ?? true,
        });
      }
    }
    return result;
  }, [layout, sections]);

  const layoutColumns: 1 | 2 = (layout[0]?.[1]?.length ?? 0) > 0 ? 2 : 1;

  const handleColumnsChange = useCallback(
    (cols: 1 | 2) => {
      const copy: string[][][] = JSON.parse(JSON.stringify(layout));
      if (cols === 1 && (copy[0]?.[1]?.length ?? 0) > 0) {
        copy[0][0] = [...(copy[0][0] ?? []), ...(copy[0][1] ?? [])];
        copy[0][1] = [];
      }
      setValue("metadata.layout", copy);
    },
    [layout, setValue],
  );

  const handleMoveSection = useCallback(
    (id: string, to: LayoutColumn) => {
      const copy: string[][][] = JSON.parse(JSON.stringify(layout));
      const fromCol = to === "sidebar" ? 0 : 1;
      const toCol = to === "sidebar" ? 1 : 0;
      const fromArr: string[] = copy[0]?.[fromCol] ?? [];
      const idx = fromArr.indexOf(id);
      if (idx === -1) return;
      fromArr.splice(idx, 1);
      if (!copy[0][toCol]) copy[0][toCol] = [];
      copy[0][toCol].push(id);
      setValue("metadata.layout", copy);
    },
    [layout, setValue],
  );

  const handleReorder = useCallback(
    (id: string, direction: -1 | 1) => {
      const copy: string[][][] = JSON.parse(JSON.stringify(layout));
      for (const page of copy) {
        for (const col of page) {
          const idx = col.indexOf(id);
          if (idx === -1) continue;
          const newIdx = idx + direction;
          if (newIdx < 0 || newIdx >= col.length) return;
          [col[idx], col[newIdx]] = [col[newIdx], col[idx]];
          setValue("metadata.layout", copy);
          return;
        }
      }
    },
    [layout, setValue],
  );

  const handleResetLayout = useCallback(() => {
    const fresh: string[][][] = JSON.parse(JSON.stringify(defaultMetadata.layout));
    const customSections: string[] = [];
    for (const page of layout) {
      for (const column of page) {
        customSections.push(...column.filter((s: string) => s.startsWith("custom.")));
      }
    }
    if (customSections.length > 0) fresh[0][0].push(...customSections);
    setValue("metadata.layout", fresh);
  }, [layout, setValue]);

  const layoutProps = useMemo(
    () => ({
      sections: layoutSections,
      columns: layoutColumns,
      onColumnsChange: handleColumnsChange,
      onMoveSection: handleMoveSection,
      onReorder: handleReorder,
      onReset: handleResetLayout,
    }),
    [
      layoutSections,
      layoutColumns,
      handleColumnsChange,
      handleMoveSection,
      handleReorder,
      handleResetLayout,
    ],
  );

  // ── Templates ──
  const currentTemplate = metadata.template;

  const handleSelectTemplate = useCallback(
    (id: string) => {
      setValue("metadata.template", id);
    },
    [setValue],
  );

  const handleProTemplate = useCallback(
    (id: string) => {
      if (hasEntitlement(tier, "premiumTemplates")) {
        setValue("metadata.template", id);
      } else {
        toast({
          variant: "info",
          title: t`Pro template`,
          description: t`Upgrade to use this premium template.`,
        });
      }
    },
    [tier, setValue, toast],
  );

  // eslint-disable-next-line lingui/no-unlocalized-strings
  const templateItems = useMemo(
    () =>
      templatesList.map((template) => ({
        id: template,
        name: template.charAt(0).toUpperCase() + template.slice(1),
        previewSlot: (
          // eslint-disable-next-line lingui/no-unlocalized-strings
          <img
            src={`/templates/jpg/${template}.jpg`}
            alt={template}
            className="h-full w-full object-cover"
          />
        ),
        pro: isProTemplate(template),
      })),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- templatesList is static
    [],
  );

  // eslint-disable-next-line lingui/no-unlocalized-strings
  const settingsTemplates: SettingsTemplate[] = useMemo(
    () =>
      templatesList.map((template) => ({
        id: template,
        name: template.charAt(0).toUpperCase() + template.slice(1),
        templateSlot: (
          // eslint-disable-next-line lingui/no-unlocalized-strings
          <img
            src={`/templates/jpg/${template}.jpg`}
            alt={template}
            className="h-full w-full object-cover"
          />
        ),
        pro: isProTemplate(template),
      })),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- templatesList is static
    [],
  );

  // ── Spacing (section, entry, line height) ──
  const onSpacingLineHeightChange = useCallback(
    (value: number) => {
      setValue("metadata.typography.lineHeight", value);
    },
    [setValue],
  );
  const onSectionSpacingChange = useCallback(
    (value: number) => {
      // FR-014: Only write when user changes the control. Create parent object if undefined.
      setValue("metadata.spacing", { ...metadata.spacing, section: value });
    },
    [metadata.spacing, setValue],
  );
  // NOTE: entry spacing is deferred (no reliable cross-template selector yet) — omitting
  // onEntrySpacingChange hides the entry slider so we don't ship a non-functional control.

  const spacingProps = useMemo(
    () => ({
      lineHeight: metadata.typography.lineHeight,
      onLineHeightChange: onSpacingLineHeightChange,
      sectionSpacing: metadata.spacing?.section,
      onSectionSpacingChange,
    }),
    [
      metadata.typography.lineHeight,
      metadata.spacing,
      onSpacingLineHeightChange,
      onSectionSpacingChange,
    ],
  ) as SpacingControlsProps;

  // ── Headings (case, weight, underline) ──
  const onCaseChange = useCallback(
    (value: string) => {
      // FR-014: Only write when user changes the control. Create parent object if undefined.
      setValue("metadata.headings", { ...metadata.headings, case: value });
    },
    [metadata.headings, setValue],
  );
  const onWeightChange = useCallback(
    (value: string) => {
      // FR-014: Only write when user changes the control. Create parent object if undefined.
      setValue("metadata.headings", { ...metadata.headings, weight: value });
    },
    [metadata.headings, setValue],
  );
  const onUnderlineChange = useCallback(
    (value: boolean) => {
      // FR-014: Only write when user changes the control. Create parent object if undefined.
      setValue("metadata.headings", { ...metadata.headings, underline: value });
    },
    [metadata.headings, setValue],
  );

  const headingsProps = useMemo(
    () => ({
      case: metadata.headings?.case,
      onCaseChange,
      weight: metadata.headings?.weight,
      onWeightChange,
      underline: metadata.headings?.underline,
      onUnderlineChange,
    }),
    [metadata.headings, onCaseChange, onWeightChange, onUnderlineChange],
  ) as HeadingsControlsProps;

  // ── Links (style) ──
  const onLinksStyleChange = useCallback(
    (value: string) => {
      // FR-014: Only write when user changes the control. Create parent object if undefined.
      setValue("metadata.links", { ...metadata.links, style: value });
    },
    [metadata.links, setValue],
  );

  const linksProps = useMemo(
    () => ({
      style: metadata.links?.style,
      onStyleChange: onLinksStyleChange,
    }),
    [metadata.links, onLinksStyleChange],
  ) as LinksControlsProps;

  // ── Header (icon style) ──
  const onIconStyleChange = useCallback(
    (value: string) => {
      // FR-014: Only write when user changes the control. Create parent object if undefined.
      setValue("metadata.header", { ...metadata.header, iconStyle: value });
    },
    [metadata.header, setValue],
  );

  const headerProps = useMemo(
    () => ({
      iconStyle: metadata.header?.iconStyle,
      onIconStyleChange,
    }),
    [metadata.header, onIconStyleChange],
  ) as HeaderControlsProps;

  return {
    themeProps,
    typographyProps,
    pageProps,
    layoutProps,
    spacingProps,
    headingsProps,
    linksProps,
    headerProps,
    currentTemplate,
    templateItems,
    settingsTemplates,
    handleSelectTemplate,
    handleProTemplate,
  };
};
